import React, { useEffect, useCallback, useRef, useState } from 'react';
import Loading from 'react-simple-loading';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import CloseIcon from '@material-ui/icons/Close';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import TextField from '@material-ui/core/TextField';
import SentimentDissatisfiedIcon from '@material-ui/icons/SentimentDissatisfied';
import { withStyles } from '@material-ui/core/styles';

import { translate } from 'services/i18nService';
import GiphyStore from 'stores/GiphyStore';
import APIStates from 'enum/APIStates';
import { generateID } from 'utils/helpers';

import giphy2 from '../../../../images/giphy2.png';

import styles from './GiphyWrapper.css';


const IDS = {
  prefix: 'Sinan018',
  element: 'giphyWrapper',
  gifButton: 'gifButton',
  closeIcon: 'closeIcon',
};

const muiStyles = () => {
  return {
    cssFocused: {},
    cssOutlinedInput: { '&$cssFocused $notchedOutline': { borderColor: '#60AAC6' } },
    notchedOutline: {},
  };
};

const makeId = generateID(IDS.prefix, IDS.element);

const GIF_OFFSET_UPPER_LIMIT = 50;
const GIF_OFFSET_BOTTOM_LIMIT = 40;
const TRIGGER_BOTTOM_SCROLL_THRESHOLD = 200;
const SEARCH_GIF_HANDLER_DELAY = 500;

const GiphyWrapper = observer((props) => {
  const { isDesktop, handleSendButton } = props;
  const [inputValue, setInputValue] = useState('');
  const {
    getTrendingGifs,
    gifs,
    openGifsAndSetAnchor,
    getSearchGifs,
    gifResponseStatus,
    setGifOffset,
    gifOffset,
    setIsLoadMoreLoading,
    isLoadMoreLoading,
    closeDrawer,
  } = GiphyStore;

  const scrollContainer = useRef();

  const hasScrolled = useCallback(
    (event) => {
      const { scrollHeight, scrollTop, clientHeight } = scrollContainer?.current || {};

      const scrollDistance = scrollHeight - scrollTop;
      const nearBottom = scrollDistance <= clientHeight + 10;
      const unTriggerBottomScroll = scrollDistance <= TRIGGER_BOTTOM_SCROLL_THRESHOLD;

      if (nearBottom && !isLoadMoreLoading && gifOffset < GIF_OFFSET_BOTTOM_LIMIT) {
        setIsLoadMoreLoading(true);
        setGifOffset();
      }
      if (event.wheelDelta < 0) {
        if (unTriggerBottomScroll) {
          event.preventDefault();
        }
      }
    },
    [isLoadMoreLoading, gifOffset, setGifOffset, setIsLoadMoreLoading]
  );

  useEffect(() => {
    if (gifOffset < GIF_OFFSET_UPPER_LIMIT) {
      if (inputValue === '') {
        getTrendingGifs(gifOffset, true);
      } else {
        getSearchGifs(inputValue, gifOffset, true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSearchGifs, getTrendingGifs, gifOffset]);

  useEffect(() => {
    let observerRefValue = null;
    if (scrollContainer.current) {
      observerRefValue = scrollContainer.current;
      scrollContainer.current.addEventListener('wheel', hasScrolled);
      scrollContainer.current.addEventListener('scroll', hasScrolled);
    }

    return () => {
      if (observerRefValue) {
        observerRefValue.removeEventListener('wheel', hasScrolled);
        observerRefValue.removeEventListener('scroll', hasScrolled);
      }
    };
  }, [hasScrolled, isLoadMoreLoading, gifOffset]);

  const searchGifsHandler = React.useMemo(
    () =>
      debounce((value) => {
        if (value === '') {
          getTrendingGifs(0);
        } else {
          getSearchGifs(value, 0);
        }
      }, SEARCH_GIF_HANDLER_DELAY),
    [getSearchGifs, getTrendingGifs]
  );

  const onChangeSearchValue = useCallback((event) => {
    setGifOffset(0);
    scrollContainer.current.scrollTop = 0;
    setIsLoadMoreLoading(false);
    setInputValue(event.target.value);
    searchGifsHandler(event.target.value);
  }, [
    searchGifsHandler,
    setGifOffset,
    setIsLoadMoreLoading,
  ]);

  const containerClasses = classNames({
    [styles.gifsContainerDesktop]: isDesktop,
    [styles.gifsContainerMobile]: !isDesktop,
  });

  const scrollClasses = classNames({
    [styles.scrollGifs]: true,
    [styles.scrollGifsNoGifs]: gifs.length === 0,
  });

  const InputProps = {
    startAdornment: (
      <InputAdornment>
        <SearchIcon />
      </InputAdornment>
    ),
    style: {
      paddingLeft: 5,
      fontSize: 14,
      marginBottom: isDesktop ? 0 : 10,
    },
    classes: {
      root: props.classes.cssOutlinedInput,
      focused: props.classes.cssFocused,
      notchedOutline: props.classes.notchedOutline,
    },
  };

  const inputProps = { style: { padding: 6 } };

  const handleContextMenu = useCallback((event) => event.preventDefault(), []);

  const onSendGif = useCallback((gifUrl, isGif, gifUrlAnalytics) => () => {
    handleSendButton(gifUrl, isGif, gifUrlAnalytics);
    if (isDesktop) {
      openGifsAndSetAnchor();
    } else {
      closeDrawer();
    }
  }, [closeDrawer, handleSendButton, isDesktop, openGifsAndSetAnchor]);

  const renderGifsContent = useCallback(() => {
    if (gifResponseStatus === APIStates.PENDING) {
      return <Loading />;
    }

    if (gifs.length > 0) {
      return (
        <div className={styles.gifsWrapper} onContextMenu={handleContextMenu}>
          {gifs.map((gif) => (
            <div key={`key-${gif.id}`} className={styles.singleGifWrap}>
              <video
                autoPlay={true}
                className={styles.gif}
                loop={true}
                muted={true}
                playsInline={true}
                src={gif.images.fixed_width.mp4}
                onClick={onSendGif(gif.id, true, gif.analytics.onsent.url)}
              />
            </div>
          ))}
        </div>
      );
    }

    if (gifResponseStatus !== APIStates.INITIAL) {
      return (
        <div className={styles.noResult}>
          <SentimentDissatisfiedIcon />
          <p>{translate('v2profile.no.matching.results')}</p>
        </div>
      );
    }

    return null;
  }, [gifResponseStatus, gifs, handleContextMenu, onSendGif]);

  return (
    <div className={containerClasses}>
      {isDesktop && (
        <div className={styles.headerGifs}>
          <h5>{translate('v2profile.send.gif')}</h5>
          <div>
            <img alt="" src={giphy2} />
            <CloseIcon className={styles.closeIcon} id={makeId(IDS.closeIcon)} onClick={openGifsAndSetAnchor} />
          </div>
        </div>
      )}
      <div ref={scrollContainer} className={scrollClasses}>
        {renderGifsContent()}
        {isLoadMoreLoading && (
          <div className={styles.loadMoreLoading}>
            <Loading />
          </div>
        )}
      </div>
      <TextField
        InputProps={InputProps}
        fullWidth={true}
        id={makeId(IDS.searchField)}
        inputProps={inputProps}
        margin="none"
        placeholder={translate('v2profile.search.gifs')}
        variant="outlined"
        onChange={onChangeSearchValue}
      />
      {!isDesktop && <img alt="" className={styles.mobileGiphyImg} src={giphy2} />}
    </div>
  );
});

GiphyWrapper.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSendButton: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool,
};

export default withStyles(muiStyles)(GiphyWrapper);
